/**
 * Ce qu'un virement SORTANT est, et ce qu'il dit.
 *
 * ## Ce que la mesure a montré
 *
 * La file des dépenses compte **590 mouvements pour 648 205,43 €** au
 * 19 septembre 2026. Sur ce total, **44 mouvements — 3 038,20 €, un demi-pour-
 * cent** — citent une référence de famille : une note de crédit ou une facture
 * d'ici.
 *
 * Tout le reste est l'argent qu'Éclosion dépense pour fonctionner : l'ONSS
 * (129 985,44 € à eux seuls), Proximus, Ethias, Monizze, les écoles
 * partenaires, les compensations aux charges, le volontariat. Ces mouvements
 * n'ont **aucune** contrepartie dans cette comptabilité-ci : ils appartiennent
 * à la comptabilité en partie double à venir.
 *
 * ## Pourquoi les deux files ne fusionnent pas
 *
 * Demandé le 19 septembre : « Ne devrait-on pas le fusionner avec les
 * entrées ? » Les chiffres disent non, et pour une raison de fond.
 *
 * La file entrante pose **une** question : à quelle facture cet argent
 * correspond-il ? La file sortante en pose **deux**, et elles n'ont rien à voir
 * l'une avec l'autre :
 *
 * - pour 44 mouvements : à quelle note de crédit ce remboursement répond-il ?
 * - pour 546 : ceci est une dépense de fonctionnement, à écarter en attendant
 *   la partie double.
 *
 * Les fondre dans un seul écran ferait entrer 645 000 € de charges dans un
 * écran de rapprochement familial. Le geste juste est l'inverse : **séparer
 * ces deux populations à l'intérieur du sortant**, pour que les 44 deviennent
 * visibles et que les 546 s'écartent par paquets.
 */

import { allLegacyRefs } from './matching';
import { normalizeReference } from './communication';
import type { LegacyRef } from './matching';

/** Ce qu'un virement sortant est, du point de vue de cette comptabilité. */
export type NatureDeLaSortie =
  /** Il cite une note de crédit ou une facture : il se rapproche. */
  | 'remboursement_famille'
  | 'volontariat'
  | 'salaires'
  | 'charges'
  | 'subvention'
  /** Tout le reste : fournisseurs, partenaires, frais. À écarter pour l'instant. */
  | 'fonctionnement';

export interface Nature {
  code: NatureDeLaSortie;
  label: string;
  /** Vrai quand ce virement concerne une famille, donc se rapproche. */
  famille: boolean;
  /** Ce qui l'a fait reconnaître. Une classification qui ne se vérifie pas ne vaut rien. */
  matched: string | null;
}

/**
 * Les motifs, dans l'ordre où ils sont essayés.
 *
 * `VOLOTARIAT` est là **avec sa faute de frappe** : c'est l'orthographe de 11
 * mouvements réels de la production. Une règle qui ne reconnaît que
 * l'orthographe correcte laisse ces onze-là dans la file pour toujours.
 */
const MOTIFS: { code: NatureDeLaSortie; label: string; motif: RegExp }[] = [
  {
    code: 'volontariat',
    label: 'Volontariat',
    motif: /\b(vol[o]?[nt]{1,2}ariat|b[eé]n[eé]vol)/i,
  },
  {
    code: 'salaires',
    label: 'Salaires et cotisations',
    motif: /\b(salaire|r[eé]mun[eé]r|onss|office national de s[eé]curit[eé]|pr[eé]compte|p[eé]cule|secr[eé]tariat social|s[eé]curit[eé] sociale)/i,
  },
  {
    code: 'charges',
    label: 'Compensation aux charges',
    motif: /\bcompensation\s+(aux\s+)?charges|\bcharges?\s*:/i,
  },
  {
    code: 'subvention',
    label: 'Subvention',
    motif: /\b(subvention|subside|\bsub\s+pad\b)/i,
  },
];

const LABELS: Record<NatureDeLaSortie, string> = {
  remboursement_famille: 'Remboursement à une famille',
  volontariat: 'Volontariat',
  salaires: 'Salaires et cotisations',
  charges: 'Compensation aux charges',
  subvention: 'Subvention',
  fonctionnement: 'Dépense de fonctionnement',
};

/**
 * Ce qu'est ce virement sortant.
 *
 * La citation d'une référence passe **avant tout le reste** : un virement qui
 * nomme `NC-26-00479` est un remboursement, quel que soit le mot qui
 * l'accompagne. L'inverse — classer d'abord par mot-clé — écarterait un
 * remboursement dont la communication dit « remboursement salaire trop perçu ».
 */
export function classerLaSortie(m: {
  communicationRaw?: string | null;
  freeText?: string | null;
  counterpartyName?: string | null;
}): Nature {
  const refs = referencesDuVirement(m.communicationRaw, m.freeText);
  if (refs.notes.length > 0 || refs.factures.length > 0) {
    const citee = refs.notes[0]?.numero ?? refs.factures[0]?.digits ?? null;
    return {
      code: 'remboursement_famille',
      label: LABELS.remboursement_famille,
      famille: true,
      matched: citee,
    };
  }

  /* Le nom du bénéficiaire compte autant que la communication : « Office
     National de Securite Sociale » ne dit rien dans sa communication, et tout
     dans son nom. */
  const texte = [m.communicationRaw, m.freeText, m.counterpartyName]
    .filter(Boolean).join(' ');

  for (const { code, label, motif } of MOTIFS) {
    const trouve = texte.match(motif);
    if (trouve) return { code, label, famille: false, matched: trouve[0] };
  }

  return { code: 'fonctionnement', label: LABELS.fonctionnement, famille: false, matched: null };
}

/** Une note de crédit citée par un virement. */
export interface NoteCitee {
  /** Le numéro normalisé : `NC-26-00479`. */
  numero: string;
  origine: 'communication' | 'releve';
}

export interface ReferencesDuVirement {
  notes: NoteCitee[];
  factures: (LegacyRef & { origine: 'communication' | 'releve' })[];
}

/**
 * Tout ce qu'un virement sortant cite : notes de crédit ET factures.
 *
 * ## Pourquoi les factures comptent autant que les notes
 *
 * Demandé le 19 septembre 2026 : « Si montant négatif et n° de facture en
 * communication, alors c'est un remboursement d'une ou de plusieurs notes de
 * crédit connectées à cette facture. »
 *
 * C'est le cas réel qui a motivé la demande : une communication qui dit
 * `CDV-NDSC-2601711 REMBOURSEMENT NC-26-00479 et NC-26-0` — coupée à 53
 * caractères en plein milieu du second numéro. La facture, elle, est entière,
 * et elle porte deux notes : `NC-26-00478` (92,00 € à rendre) et `NC-26-00479`
 * (95,00 €, déjà remboursée). Lire la facture, c'est retrouver la note que la
 * troncature a emportée.
 *
 * Les factures sont lues avec `allLegacyRefs`, **la fonction du rapprochement
 * entrant** : les deux sens doivent reconnaître les mêmes références, sinon
 * une même communication se lit différemment selon le côté du compte.
 */
export function referencesDuVirement(
  communication?: string | null,
  libelleDuReleve?: string | null,
): ReferencesDuVirement {
  const notes: NoteCitee[] = [];
  const factures: ReferencesDuVirement['factures'] = [];
  const vuesNotes = new Set<string>();
  const vuesFactures = new Set<string>();

  const lire = (texte: string | null | undefined, origine: 'communication' | 'releve') => {
    if (!texte) return;

    /* Les notes, sur le texte NORMALISÉ : `NC 26 00479`, `nc-26-00479` et
       `NC26-00479` deviennent la même chaîne, comme pour les factures. */
    for (const m of normalizeReference(texte).matchAll(/NC(\d{2})(\d{5})/g)) {
      const numero = `NC-${m[1]}-${m[2]}`;
      if (vuesNotes.has(numero)) continue;
      vuesNotes.add(numero);
      notes.push({ numero, origine });
    }

    for (const r of allLegacyRefs(texte)) {
      if (vuesFactures.has(r.digits)) continue;
      vuesFactures.add(r.digits);
      factures.push({ ...r, origine });
    }
  };

  lire(communication, 'communication');
  lire(libelleDuReleve, 'releve');
  return { notes, factures };
}

/** Une note de crédit citée, et ce que la base en dit. */
export interface NoteResolue {
  numero: string;
  origine: 'communication' | 'releve';
  /** Par quoi elle est arrivée : citée telle quelle, ou par la facture citée. */
  parLaFacture: string | null;
  creditNoteId: string | null;
  userId: string | null;
  famille: string | null;
  invoiceNumber: string | null;
  montantCents: number;
  dejaRembourseCents: number;
  /** Ce qu'elle peut encore recevoir : montant moins déjà remboursé. */
  remboursableCents: number;
  /** Ce que CE virement lui a déjà donné. */
  deCeVirementCents: number;
  destinee: string | null;
}

export interface LectureDeLaSortie {
  aPlacerCents: number;
  /** Ce que les notes citées peuvent encore recevoir. */
  remboursableCents: number;
  ecartCents: number;
  /** Les numéros cités qu'aucune note ne porte. */
  inconnues: string[];
  familles: string[];
  phrases: string[];
}

export function lireLaSortie(
  aPlacerCents: number,
  notes: NoteResolue[],
): LectureDeLaSortie {
  const connues = notes.filter((n) => n.creditNoteId !== null);
  const inconnues = notes.filter((n) => n.creditNoteId === null).map((n) => n.numero);
  const remboursableCents = connues.reduce((s, n) => s + n.remboursableCents, 0);
  const ecartCents = aPlacerCents - remboursableCents;
  const familles = [...new Set(connues.map((n) => n.famille).filter((f): f is string => !!f))];
  const rendues = connues.filter((n) => n.remboursableCents === 0);

  const phrases: string[] = [`${eurosCourts(aPlacerCents)} à placer.`];

  if (connues.length === 0) {
    phrases.push(
      inconnues.length > 0
        ? 'Aucun des numéros cités ne désigne une note de crédit de cette comptabilité.'
        : 'Aucune note de crédit n’est citée par ce virement.',
    );
  } else if (remboursableCents > 0) {
    phrases.push(
      `Les notes citées peuvent encore recevoir ${eurosCourts(remboursableCents)}.`,
    );
  } else {
    phrases.push('Les notes citées sont déjà entièrement remboursées.');
  }

  if (connues.length > 0) {
    if (ecartCents > 0) {
      phrases.push(
        `Il resterait ${eurosCourts(ecartCents)} que les notes citées ne réclament pas`
        + `${rendues.length > 0 ? ' — certaines ont déjà été remboursées.' : '.'}`,
      );
    } else if (ecartCents < 0) {
      phrases.push(
        `Ce virement ne couvre pas tout : il manque ${eurosCourts(-ecartCents)} pour solder `
        + 'les notes citées.',
      );
    } else {
      phrases.push('Le compte tombe juste : ce virement solde exactement les notes citées.');
    }
  }

  if (familles.length > 1) {
    phrases.push(
      `Attention : les notes citées appartiennent à ${familles.length} familles différentes — `
      + `${familles.join(', ')}.`,
    );
  }

  if (inconnues.length > 0 && connues.length > 0) {
    phrases.push(
      inconnues.length === 1
        ? `${inconnues[0]} ne correspond à aucune note de crédit connue.`
        : `${inconnues.length} numéros cités ne correspondent à aucune note connue : `
          + `${inconnues.join(', ')}.`,
    );
  }

  return { aPlacerCents, remboursableCents, ecartCents, inconnues, familles, phrases };
}

/**
 * Ce que ce virement peut encore payer, note par note.
 *
 * Dans l'ordre où elles sont citées, chacune plafonnée à ce qu'elle peut
 * recevoir, l'ensemble plafonné à ce qu'il reste à placer. Rien n'est écrit :
 * l'écran montre ces lignes et un humain décide.
 */
export function partsDeLaSortie(
  aPlacerCents: number,
  notes: NoteResolue[],
): { note: NoteResolue; montantCents: number }[] {
  const parts: { note: NoteResolue; montantCents: number }[] = [];
  let reste = aPlacerCents;
  for (const n of notes) {
    if (reste <= 0) break;
    if (!n.creditNoteId || n.remboursableCents <= 0) continue;
    const part = Math.min(reste, n.remboursableCents);
    parts.push({ note: n, montantCents: part });
    reste -= part;
  }
  return parts;
}

/* Le format court, pour des phrases. `money.ts` sert à l'affichage des tableaux. */
function eurosCourts(centimes: number): string {
  return `${(centimes / 100).toFixed(2).replace('.', ',')} €`;
}
